import React, { useCallback, useContext, useEffect, useMemo } from "react"
import { NewReservationContext } from "~/contexts/admin/NewReservationContext"
import { AdminContext } from "~/contexts/AdminContext"
import isValidEmail from "~/utils/isValidEmail"
import isValidPhoneNumber from "~/utils/isValidPhoneNumber"
import eSendingMode from "~/enums/eSendingMode"
import ePaymentType from "~/enums/ePaymentType"
import { RESERVATION_KINDS, STATUSES } from "~/constants"
import Button from "~/components/ui/Button"
import HoverTip from "~/components/ui/HoverTip"
import { CreateReservationSteps, ReservationOptions } from "~/constants/NewReservation"
import { isMobile } from "react-device-detect"

interface IComponentProps {
  step: CreateReservationSteps
  setStep: React.Dispatch<React.SetStateAction<CreateReservationSteps>>
  isProcessing: boolean
  setErrorResponse: any
  closeModal: () => void
  errorResponse: string
  isCardInputValid: boolean
}

const NewReservationModalToolbar = (props: IComponentProps) => {
  const {
    newReservation,
    selectedReservationType,
    recurringKind,
    isPayingSeparately,
    order,
    inputEmail,
    inputPhone,
    createReservation,
  } = useContext(NewReservationContext)
  const { selectedSendingMode, closeModal } = useContext(AdminContext)

  const validInputEmail = inputEmail && isValidEmail(inputEmail)
  const validInputPhone = inputPhone && isValidPhoneNumber(inputPhone)
  const canPaymentLink = !!((validInputEmail || validInputPhone) && selectedSendingMode != eSendingMode.NONE)

  useEffect(() => {
    console.log(canPaymentLink)
  }, [canPaymentLink, selectedSendingMode])

  const canCreate = useMemo(() => {
    if (props.isProcessing || props.errorResponse || !order) {
      return false
    }

    switch (newReservation.paymentMethod) {
      case ePaymentType.LINK:
        return canPaymentLink
      case ePaymentType.CARD_INPUT:
        return props.isCardInputValid
      case ePaymentType.CARD_READER:
        return true
      default:
        return true
    }
  }, [
    newReservation.paymentMethod,
    props.isCardInputValid,
    props.isProcessing,
    props.errorResponse,
    order,
    canPaymentLink,
  ])

  const submitButtonExtraText = () => {
    if (newReservation.kind === RESERVATION_KINDS.GROUP && isPayingSeparately) return "group"
    if (newReservation.paymentMethod === ePaymentType.CASH_UNPAID) return "unpaid"
    if (newReservation.paymentMethod === ePaymentType.CASH_PAID) return "paid"
    return ""
  }

  const handleCreateReservation = useCallback(
    async (options?: ReservationOptions) => {
      const resp = await createReservation(options)

      if (resp) {
        closeModal()
        props.closeModal()
      }
    },
    [createReservation, closeModal, props],
  )

  const handleBackClick = useCallback(() => {
    props.setErrorResponse(null)
    props.setStep(CreateReservationSteps.reservationInfo)
  }, [props])

  const handleCancelClick = useCallback(() => {
    props.setErrorResponse(null)
    props.closeModal()
  }, [props])

  return (
    <div className="xs:sticky md:static bottom-0 left-0 right-0 flex flex-col custom-500:flex-row justify-between items-start custom-500:items-center md:items-end py-4 -mx-4 -mb-4 sm:-mx-6 sm:-mb-6 bg-gray-50 border-t border-gray-200 px-6 rounded-b-lg">
      {props.step === CreateReservationSteps.paymentInfo || newReservation.status === RESERVATION_KINDS.BLOCKED ? (
        <div className="flex gap-3 w-full justify-between">
          <Button
            variant="white"
            size="large"
            disabled={props.isProcessing}
            className="w-24 sm:w-32"
            onClick={handleCancelClick}
          >
            Cancel
          </Button>
          <div className="flex gap-2">
            {props.step === CreateReservationSteps.paymentInfo && (
              <Button
                variant="white"
                size="large"
                disabled={props.isProcessing}
                className="w-24 sm:w-32"
                onClick={handleBackClick}
              >
                Back
              </Button>
            )}

            {selectedReservationType === STATUSES.BLOCKED ? (
              <Button
                variant="blue"
                size="large"
                className="w-40"
                disabled={props.isProcessing || props.errorResponse}
                onClick={handleCreateReservation}
              >
                Create blocker
              </Button>
            ) : (
              <HoverTip
                content={
                  selectedSendingMode == eSendingMode.NONE
                    ? "In order to send a payment link, you must send a confirmation message"
                    : "Please add a valid phone or email to send the payment link to"
                }
                active={newReservation.paymentMethod == ePaymentType.LINK && !canPaymentLink}
              >
                <Button
                  variant="blue"
                  size="large"
                  disabled={!canCreate}
                  onClick={() =>
                    handleCreateReservation({
                      isPrePaid: newReservation.paymentMethod === ePaymentType.CASH_PAID,
                      withLink: newReservation.paymentMethod === ePaymentType.LINK,
                      isGroupSeparated: newReservation.kind === RESERVATION_KINDS.GROUP && isPayingSeparately,
                      recurringKind,
                    })
                  }
                >
                  Create {!isMobile && `${submitButtonExtraText()} reservation`}
                </Button>
              </HoverTip>
            )}
          </div>
        </div>
      ) : (
        props.step === CreateReservationSteps.reservationInfo && (
          <div className="flex w-full justify-between">
            <Button
              variant="white"
              size="large"
              disabled={props.isProcessing}
              className="w-24 sm:w-32"
              onClick={() => {
                props.setErrorResponse(null)
                props.closeModal()
              }}
            >
              Cancel
            </Button>
            <Button
              variant="blue"
              size="large"
              disabled={props.isProcessing || props.errorResponse || !order}
              onClick={() => {
                props.setStep(CreateReservationSteps.paymentInfo)
              }}
              className="w-24 sm:w-32"
            >
              Next
            </Button>
          </div>
        )
      )}
    </div>
  )
}

export default NewReservationModalToolbar
